import { useFormik } from "formik";
import ModalComponent from "..";
import { useUpdateHolidayStatusMutation } from "../../../redux/features/admin/admin.api";
import { formatDate, parseDateStringToDate } from "../../../utils/helpers";
import { BaseApiErrorResponse, ModalCompProps } from "../../../utils/types";
import { Button } from "../../buttons/button";
import { HolidayDateBadge } from "../../dateBadge";
import { DateInput } from "../../inputs/dateInput";
import { InfoComponent } from "../../notification/infoComponent";
import { HolidaySchema } from "../../../utils/validations";
import moment from "moment";

export const ConfirmPublishedHoliday = ({
  open,
  handleClose,
  onSuccess,
  holidayName,
  holidayId,
  holidayDate,
  nextWorkingDay,
  refetchHolidays,
}: ModalCompProps & {
  onSuccess: () => void;
  holidayName: string;
  holidayId: string;
  nextWorkingDay: string;
  holidayDate: string;
  refetchHolidays: () => void;
}) => {
  const [updateHolidayStatus, { isLoading, isError, error }] =
    useUpdateHolidayStatusMutation();

  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    handleSubmit,
    // handleChange,
    resetForm,
    isValid,
  } = useFormik({
    initialValues: {
      name: holidayName,
      holidayDate: holidayDate,
      nextWorkingDay: nextWorkingDay,
      status: "confimed",
    },
    enableReinitialize: true,
    validationSchema: HolidaySchema,
    onSubmit: async (vals) => {
      const resp = await updateHolidayStatus({
        holidayId,
        payload: {
          status: "confirmed",
        },
      }).unwrap();
      if (resp.status === "success") {
        onSuccess();
        handleClose();
        resetForm();
        refetchHolidays();
      }
    },
  });

  return (
    <ModalComponent
      open={open}
      handleClose={() => {
        handleClose();
      }}
      className=""
      dialogClassName=""
      closeOnOverlayClick
      showHeader
      showIcon={false}
      iconWidth={21}
      iconHeight={21}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {isError && (
          <InfoComponent
            title="Error"
            text={
              (error as unknown as BaseApiErrorResponse)?.data?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}
        <div className="flex flex-col gap-2.5">
          <div className="w-[50px]">
            <HolidayDateBadge holidayDate={holidayDate} />
          </div>
          <div className="flex flex-col gap-1">
            <p className="font-bold text-grey-950">{holidayName}</p>
            <p className="text-sm text-grey-600">
              Work resumes {formatDate(nextWorkingDay)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <DateInput
            name="holidayDate"
            onBlur={handleBlur}
            placeholder="Choose Holiday Date"
            errors={
              errors.holidayDate && touched.holidayDate
                ? errors.holidayDate
                : ""
            }
            disabled={true}
            title="Holiday Date"
            date={parseDateStringToDate(values.holidayDate, undefined, true)}
            setDate={(date) => {
              setFieldValue(
                "holidayDate",
                date ? new Date(date).toISOString() : "",
              );
            }}
          />

          <DateInput
            name="nextWorkingDay"
            onBlur={handleBlur}
            placeholder="Choose working day"
            errors={
              errors.nextWorkingDay && touched.nextWorkingDay
                ? errors.nextWorkingDay
                : ""
            }
            title="Next Working Day"
            date={parseDateStringToDate(values.nextWorkingDay, undefined, true)}
            minDate={
              values.holidayDate !== "" ? moment(values.holidayDate) : undefined
            }
            disabled={true}
            setDate={(date) => {
              setFieldValue(
                "nextWorkingDay",
                date ? new Date(date).toISOString() : "",
              );
            }}
          />
        </div>
        <div className="col-span-full flex items-center justify-end gap-2">
          <Button
            text={"Cancel"}
            type="button"
            onClick={() => {
              handleClose();
            }}
            variation="plain-grey"
            className="max-w-fit"
          />
          <Button
            text={"Confirm Holiday"}
            type="submit"
            className="max-w-fit"
            loading={isLoading}
            disabled={isLoading || !isValid}
            variation={"primary"}
          />
        </div>
      </form>
    </ModalComponent>
  );
};
